import React from 'react';
import { Tag, Sparkles, X } from 'lucide-react';

export interface CouponField {
  code?: string;
  discountType: 'Percentage' | 'Fixed';
  value: number;
  active: boolean;
  expiryDate?: string;
}

interface CouponSectionProps {
  coupon?: CouponField | null;
  onChange: (coupon: CouponField | null) => void;
  itemName?: string;
}

const generateCode = (itemName?: string) => {
  const base = (itemName || 'CUPOM')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 6);
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `${base || 'CUPOM'}-${rand}`;
};

export const CouponSection: React.FC<CouponSectionProps> = ({ coupon, onChange, itemName }) => {
  const enabled = !!coupon && !!coupon.code;

  const set = (patch: Partial<CouponField>) => {
    onChange({ code: '', discountType: 'Percentage', value: 0, active: true, expiryDate: '', ...coupon, ...patch });
  };

  const isExpired = enabled && coupon?.expiryDate && new Date(coupon.expiryDate) < new Date();
  const status = !enabled ? null : !coupon?.active ? { label: 'Inativo', cls: 'bg-gray-100 text-gray-500' } : isExpired ? { label: 'Expirado', cls: 'bg-red-100 text-red-600' } : { label: 'Ativo', cls: 'bg-green-100 text-green-700' };

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Tag size={14} className="text-purple-600" />
          <span className="text-[10px] font-black text-gray-700 uppercase tracking-widest">Cupão Associado</span>
        </div>
        {enabled ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="flex items-center gap-1 text-[10px] font-bold text-red-500 hover:text-red-700 uppercase"
          >
            <X size={12} /> Remover
          </button>
        ) : (
          <button
            type="button"
            onClick={() => set({ code: generateCode(itemName) })}
            className="flex items-center gap-1 text-[10px] font-bold text-purple-600 hover:text-purple-800 uppercase"
          >
            <Sparkles size={12} /> Criar Cupão
          </button>
        )}
      </div>

      {enabled && coupon && (
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Código do Cupão</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={coupon.code || ''}
                  onChange={e => set({ code: e.target.value.toUpperCase() })}
                  placeholder="EX: NATAL10, BEMVINDO"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-bold uppercase tracking-wider outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  type="button"
                  onClick={() => set({ code: generateCode(itemName) })}
                  title="Gerar código automaticamente"
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center"
                >
                  <Sparkles size={14} className="text-purple-600" />
                </button>
              </div>
              <p className="text-[9px] text-gray-400 mt-1">Use nomes reais do cupão (ex: ANIVERSARIO, PROMO10) ou gere automaticamente.</p>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Status</label>
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                <input
                  type="checkbox"
                  checked={coupon.active}
                  onChange={e => set({ active: e.target.checked })}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <span className="text-xs font-bold text-gray-700">Cupão Ativo</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Tipo de Desconto</label>
              <select
                value={coupon.discountType}
                onChange={e => set({ discountType: e.target.value as 'Percentage' | 'Fixed' })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="Percentage">Percentagem (%)</option>
                <option value="Fixed">Valor Fixo (MT)</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                Valor {coupon.discountType === 'Percentage' ? '(%)' : '(MT)'}
              </label>
              <input
                type="number"
                min="0"
                value={coupon.value || 0}
                onChange={e => set({ value: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Validade (opcional)</label>
              <input
                type="date"
                value={coupon.expiryDate || ''}
                onChange={e => set({ expiryDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          {status && (
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[9px] font-black uppercase ${status.cls}`}>
                {status.label}
              </span>
              <span className="text-[11px] text-gray-500">
                {coupon.discountType === 'Percentage' ? `${coupon.value}% de desconto` : `${(coupon.value || 0).toLocaleString('pt-MZ')} MT de desconto`}
                {coupon.expiryDate && ` até ${new Date(coupon.expiryDate).toLocaleDateString('pt-MZ')}`}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
